// src/hooks/usePermission.js
import { useEffect, useState, useCallback } from 'react';
import userRoleUtils from '../utils/userRoleUtils';
import userImpersonationService from '../services/userImpersonationService';

/**
 * Custom hook for checking user permissions with impersonation support
 * 
 * @returns {Object} Object containing permission check functions and state
 */
const usePermission = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  
  // Update state function that can be used in different places
  const updateState = useCallback(() => {
    setIsAdmin(userRoleUtils.isAdmin());
    setIsImpersonating(userImpersonationService.isImpersonating());
    
    // Get current permissions
    const permissions = JSON.parse(localStorage.getItem('user_permissions') || '[]');
    const fallbackPermissions = userRoleUtils.getFallbackPermissionsForCurrentUser();
    setUserPermissions([...new Set([...permissions, ...fallbackPermissions])]);
    
    if (userImpersonationService.isImpersonating()) {
      setImpersonatedUser(userImpersonationService.getImpersonatedUser());
      setAdminUser(userImpersonationService.getAdminUser());
    } else {
      setImpersonatedUser(null);
      setAdminUser(null);
    }
  }, []);
  
  // Initialize state
  useEffect(() => {
    updateState();
    
    // Set up event listener to detect impersonation changes
    const handleStorageChange = (e) => {
      if (e.key === 'user' || 
          e.key === 'is_impersonating' || 
          e.key === 'user_permissions' || 
          e.key === 'user_permissions_full' ||
          e.key === 'user_roles' ||
          e.key === 'user_roles_full' ||
          e.key === '_admin_user') {
        updateState();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Custom event for permission changes
    window.addEventListener('permissionsChanged', updateState);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('permissionsChanged', updateState);
    };
  }, [updateState]);
  
  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check
   * @returns {boolean} True if user has permission
   */
  const hasPermission = useCallback((permission) => {
    // If permission is empty, grant access
    if (!permission || permission === '') return true;
    
    // Admin always has all permissions
    if (isAdmin) return true;
    
    // Check permissions array
    return userPermissions.includes(permission) || userRoleUtils.hasPermission(permission);
  }, [isAdmin, userPermissions]);
  
  /**
   * Check if user has any of the specified permissions
   * @param {Array} permissions - Array of permissions to check
   * @returns {boolean} True if user has any of the specified permissions
   */
  const hasAnyPermission = useCallback((permissions) => {
    // If permissions array is empty, grant access
    if (!permissions || permissions.length === 0) return true;
    
    // Admin always has all permissions
    if (isAdmin) return true;
    
    // Check if user has any of the permissions
    return permissions.some(permission => userPermissions.includes(permission));
  }, [isAdmin, userPermissions]);
  
  /**
   * Check if user has all of the specified permissions
   * @param {Array} permissions - Array of permissions to check
   * @returns {boolean} True if user has all of the specified permissions
   */
  const hasAllPermissions = useCallback((permissions) => {
    // If permissions array is empty, grant access
    if (!permissions || permissions.length === 0) return true;
    
    // Admin always has all permissions
    if (isAdmin) return true;
    
    // Check if user has all of the permissions
    return permissions.every(permission => userPermissions.includes(permission));
  }, [isAdmin, userPermissions]);
  
  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean} True if user has the role
   */
  const hasRole = useCallback((role) => {
    return userRoleUtils.hasRole(role);
  }, []);
  
  /**
   * Start impersonating a user
   * @param {number|string} userId - User ID to impersonate
   * @returns {Promise<Object>} Result of impersonation attempt
   */
  const startImpersonation = useCallback(async (userId) => {
    const result = await userImpersonationService.impersonateUser(userId);
    if (result.success) {
      updateState();
    }
    return result;
  }, [updateState]);
  
  /**
   * Stop impersonating a user
   * @returns {Object} Result of ending impersonation
   */
  const stopImpersonation = useCallback(() => {
    const result = userImpersonationService.stopImpersonation();
    if (result.success) {
      updateState();
    }
    return result;
  }, [updateState]);
  
  /**
   * Get all user permissions
   * @returns {Array} Array of permission names
   */
  const getAllPermissions = useCallback(() => {
    return userPermissions;
  }, [userPermissions]);
  
  return {
    isAdmin,
    isImpersonating,
    impersonatedUser,
    adminUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    getAllPermissions,
    startImpersonation,
    stopImpersonation
  };
};

export default usePermission;
